import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { OrganizerProfile, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { useTableState } from '@/hooks/useTableState';
import { DataTablePagination } from '@/components/tables/DataTablePagination';
import { MOCK_USERS_BY_ROLE } from '@/mockData'; // Import mock data

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>{children}
  </button>
);

const TableActions: React.FC<{ 
  organizer: OrganizerProfile;
  role: UserRole; 
  onAction: (action: string, organizerId: string) => void;
}> = ({ organizer, role, onAction }) => {

  const handleAction = (action: string) => {
    onAction(action, organizer.id);
  };

  // Ensure Edit and Delete buttons are shown for admin roles
  if (role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('edit')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">Edit</ActionButton>
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }
  
  return <></>; // Return an empty fragment instead of null
};

const OrganizerRealDataTable = ({ currentUserRole }: { currentUserRole: UserRole }) => {
  const [organizers, setOrganizers] = useState<OrganizerProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { notify } = useToast();

  const {
    pageIndex,
    pageSize,
    setPageIndex,
    setPageSize,
    getPaginatedData,
  } = useTableState<OrganizerProfile>(organizers, 10);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('/roster-members.json');
        if (!response.ok) {
          throw new Error(`Failed to fetch organizers: ${response.statusText}`);
        }
        const data: OrganizerProfile[] = await response.json();
        setOrganizers(data.filter((user: OrganizerProfile) => user.role === UserRole.ORGANIZER));
        notify('Successfully loaded real-time organizer data.', 'success');
      } catch (e) {
        console.error("API Error:", (e as Error).message);
        // Fallback to mock data
        notify('Could not load real-time data. Displaying mock data instead.', 'warn');
        const mockOrganizers = Object.values(MOCK_USERS_BY_ROLE).filter(
          (user) => user.role === UserRole.ORGANIZER
        ) as unknown as OrganizerProfile[];
        setOrganizers(mockOrganizers);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [notify]);

  const handleAction = (action: string, organizerId: string) => {
    notify(`Action '${action}' triggered for organizer '${organizerId}'.`, 'info');
    // Handle the delete action by filtering the state
    if (action === 'delete') {
      setOrganizers(currentOrganizers => currentOrganizers.filter(o => o.id !== organizerId));
      notify(`Organizer ${organizerId} was successfully deleted.`, 'success');
    }
  };

  if (isLoading) return <div className="text-center p-8 text-kala-400">Loading organizer data...</div>;

  const paginatedOrganizers = getPaginatedData();
  const pageCount = Math.ceil(organizers.length / pageSize);

  return (
    <div>
      <Table className="bg-kala-900 text-white rounded-lg p-6">
        <TableCaption>Real-time organizer data. {pageCount > 1 && `(Page ${pageIndex + 1} of ${pageCount})`}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Events Managed</TableHead>
            <TableHead>Verified</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedOrganizers.map((organizer) => (
            <TableRow key={organizer.id}>
              <TableCell>{organizer.user?.name || 'N/A'}</TableCell>
              <TableCell>{organizer.pastEvents?.length || 0}</TableCell>
              <TableCell>{organizer.verified ? 'Yes' : 'No'}</TableCell>
              <TableCell className="text-right">
                  <TableActions organizer={organizer} role={currentUserRole} onAction={handleAction} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DataTablePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
        />
    </div>
  );
};

export default OrganizerRealDataTable;
